import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DeliveryBadge, StatusBadge, fmtDate, inr } from "@/components/badges";

export default function AdminDashboard() {
  const [tab, setTab] = useState("approvals");
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [zones, setZones] = useState([]);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [nz, setNz] = useState({ name: "", express_supported: true });

  const loadVendors = () => api.get("/admin/vendors").then((r) => setVendors(r.data)).catch(() => {});
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadZones = () => api.get("/admin/zones").then((r) => setZones(r.data)).catch(() => {});
  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => {
    loadVendors(); loadOrders(); loadZones(); loadStats();
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  const approve = async (vid) => {
    try {
      await api.post(`/admin/vendors/${vid}/approve`);
      toast.success("Vendor approved");
      loadVendors(); loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addZone = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/zones", nz);
      toast.success("Zone added");
      setNz({ name: "", express_supported: true });
      loadZones();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleZone = async (zid) => {
    try { await api.post(`/admin/zones/${zid}/toggle`); loadZones(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const pendingVendors = vendors.filter((v) => !v.approved);
  const breached = orders.filter((o) => o.sla_breach || o.dispatch_breach);
  const TABS = [["approvals", "Approvals"], ["orders", "Orders"], ["zones", "Zones"], ["reports", "Reports"], ["leads", "Leads"]];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      <p className="overline-label">Admin Hub</p>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Operations control</h1>

      <div className="mt-6 flex gap-2 border-b border-slate-200 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-orange-700 text-orange-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {l}{k === "approvals" && pendingVendors.length > 0 && <span className="ml-2 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{pendingVendors.length}</span>}
            {k === "orders" && breached.length > 0 && <span className="ml-2 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-slate-900">{breached.length}</span>}
          </button>
        ))}
      </div>

      {tab === "approvals" && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="card-surface p-5" data-testid="vendor-approvals">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Vendor approvals</h2>
            {pendingVendors.length === 0 && <p className="mt-3 text-sm text-slate-400">No pending vendors.</p>}
            {pendingVendors.map((v) => (
              <div key={v.id} className="mt-3 rounded-lg border border-slate-200 p-4" data-testid={`pending-vendor-${v.id}`}>
                <p className="font-semibold text-slate-900">{v.shop_name}</p>
                <p className="text-xs text-slate-500 mt-1">{v.contact_name} · {v.email} · GSTIN {v.gstin || "—"}</p>
                <p className="text-xs text-slate-500">{v.address}</p>
                <button onClick={() => approve(v.id)} data-testid={`approve-${v.id}`} className="btn-dark mt-3 !px-3 !py-2 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
              </div>
            ))}
            {vendors.filter((v) => v.approved).length > 0 && (
              <>
                <h3 className="mt-6 text-xs font-bold uppercase tracking-wider text-slate-400">Approved</h3>
                {vendors.filter((v) => v.approved).map((v) => (
                  <p key={v.id} className="mt-2 text-sm text-slate-700">{v.shop_name} <span className="text-xs text-slate-400">· {v.commission_pct}% commission · rating {v.rating}</span></p>
                ))}
              </>
            )}
          </div>
          <div className="card-surface p-5" data-testid="users-panel">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Users ({users.length})</h2>
            <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2" data-testid={`user-row-${u.email}`}>
                  <div>
                    <p className="font-semibold text-slate-900">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email} · {u.company || "—"}</p>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${u.role === "admin" ? "border-slate-900 bg-slate-900 text-white" : u.role === "vendor" ? "border-amber-500/40 bg-amber-500/10 text-amber-700" : "border-slate-300 text-slate-600"}`}>{u.role}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-6 space-y-3" data-testid="admin-orders">
          {orders.length === 0 && <p className="text-sm text-slate-400">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.id} className={`card-surface p-4 ${o.sla_breach || o.dispatch_breach ? "!border-red-400" : ""}`} data-testid={`admin-order-${o.order_no}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono2 text-sm font-bold">{o.order_no}</span>
                <StatusBadge status={o.status} />
                <DeliveryBadge mode={o.mode} />
                {(o.sla_breach || o.dispatch_breach) && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-red-400 bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700" data-testid={`breach-${o.order_no}`}>
                    <AlertTriangle className="h-3 w-3" /> SLA breach
                  </span>
                )}
                <span className="ml-auto font-display font-bold">{inr(o.total)}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{fmtDate(o.created_at)} · {o.buyer_company || o.buyer_name} → {o.vendor_name} · {o.items.length} items · {o.payment_method.toUpperCase()}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "zones" && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="card-surface p-5" data-testid="zones-list">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Service zones — Bengaluru</h2>
            {zones.map((z) => (
              <div key={z.id} className="mt-3 flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`zone-row-${z.id}`}>
                <span className="text-sm font-semibold text-slate-900 flex items-center gap-2"><MapPin className="h-4 w-4 text-orange-700" />{z.name}</span>
                <button onClick={() => toggleZone(z.id)} data-testid={`zone-toggle-${z.id}`}
                  className={`rounded-full border px-3 py-1 text-[11px] font-bold ${z.express_supported ? "border-amber-500/40 bg-amber-500/10 text-amber-700" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"}`}>
                  {z.express_supported ? "Express ON" : "Scheduled only"}
                </button>
              </div>
            ))}
          </div>
          <form onSubmit={addZone} className="card-surface p-5 h-fit" data-testid="add-zone-form">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Add zone</h2>
            <input required placeholder="Zone name" value={nz.name} onChange={(e) => setNz({ ...nz, name: e.target.value })} className="input-b2b mt-3" data-testid="zone-name-input" />
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={nz.express_supported} onChange={(e) => setNz({ ...nz, express_supported: e.target.checked })} className="h-4 w-4 accent-amber-500" data-testid="zone-express-toggle" />
              Express-enabled
            </label>
            <button type="submit" className="btn-brand mt-4" data-testid="zone-add-btn">Add zone</button>
          </form>
        </div>
      )}

      {tab === "reports" && stats && (
        <div className="mt-6 space-y-6" data-testid="admin-reports">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["GMV", inr(stats.gmv)], ["Orders", stats.total_orders], ["Express share", `${stats.express_share}%`],
              ["Cancellation rate", `${stats.cancellation_rate}%`], ["Active buyers", stats.active_buyers], ["Active vendors", stats.active_vendors],
              ["Pending vendors", stats.pending_vendors], ["Leads", stats.leads]].map(([l, v]) => (
              <div key={l} className="card-surface p-5"><p className="font-display text-2xl font-bold text-slate-900">{v}</p><p className="text-xs uppercase tracking-wider text-slate-500 mt-1">{l}</p></div>
            ))}
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="card-surface p-5">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Top SKUs by qty</h3>
              {stats.top_skus.length === 0 && <p className="mt-3 text-sm text-slate-400">No sales yet.</p>}
              {stats.top_skus.map((s) => (
                <div key={s.name} className="mt-2 flex justify-between text-sm"><span className="text-slate-700 truncate pr-2">{s.name}</span><span className="font-bold">{s.qty}</span></div>
              ))}
            </div>
            <div className="card-surface p-5">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Vendor SLA</h3>
              {stats.vendor_stats.map((v) => (
                <div key={v.shop_name} className="mt-2 text-sm">
                  <p className="font-semibold text-slate-900">{v.shop_name}</p>
                  <p className="text-xs text-slate-500">{v.orders} orders · acceptance {v.acceptance_rate == null ? "—" : `${v.acceptance_rate}%`} · <span className={v.sla_breaches > 0 ? "text-red-600 font-semibold" : ""}>{v.sla_breaches} breaches</span></p>
                </div>
              ))}
            </div>
            <div className="card-surface p-5">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Demand by zone</h3>
              {stats.zone_distribution.length === 0 && <p className="mt-3 text-sm text-slate-400">No orders yet.</p>}
              {stats.zone_distribution.map((z) => (
                <div key={z.zone} className="mt-2 flex justify-between text-sm"><span className="text-slate-700">{z.zone}</span><span className="font-bold">{z.orders}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "leads" && (
        <div className="mt-6 space-y-3" data-testid="admin-leads">
          {leads.length === 0 && <p className="text-sm text-slate-400">No onboarding leads yet.</p>}
          {leads.map((l) => (
            <div key={l.id} className="card-surface p-4" data-testid={`lead-${l.id}`}>
              <div className="flex items-center gap-3">
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${l.type === "vendor" ? "border-amber-500/40 bg-amber-500/10 text-amber-700" : "border-blue-500/40 bg-blue-500/10 text-blue-700"}`}>{l.type}</span>
                <p className="font-semibold text-slate-900">{l.name}</p>
                <p className="text-xs text-slate-500">{l.company}</p>
                <p className="ml-auto text-xs text-slate-400">{fmtDate(l.created_at)}</p>
              </div>
              <p className="mt-1 text-xs text-slate-500">{l.email} · {l.phone}</p>
              {l.message && <p className="mt-1 text-sm text-slate-600">{l.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
